import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { X, ImagePlus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { formatSafeDate } from "@/lib/date-utils";
import { Label } from '@/components/ui/label';
import { CalendarIcon } from '@/components/ui/icons';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ScrollArea } from '@/components/ui/scroll-area';
import { clientService } from '@/lib/supabase/clients';

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  notes: string;
}

// Define local interface for Trip
interface Trip {
  id: string;
  title?: string;
  clientIds: string[];
  destination: string[] | string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  status: string;
  notes?: string;
  coverImages?: string[];
  client?: string | string[];
}

interface TripBasicInfoFormProps {
  trip: Trip;
  handleChange: (field: keyof Trip, value: any) => void;
  isEditing?: boolean;
}

// Lista de países (reutilizada do componente de itinerário)
const countries = [
  "Afeganistão", "África do Sul", "Albânia", "Alemanha", "Andorra", "Angola", "Antígua e Barbuda", "Arábia Saudita", 
  "Argélia", "Argentina", "Armênia", "Austrália", "Áustria", "Azerbaijão", "Bahamas", "Bangladesh", "Barbados", 
  "Barein", "Bélgica", "Belize", "Benin", "Bielorrússia", "Bolívia", "Bósnia e Herzegovina", "Botsuana", "Brasil", 
  "Brunei", "Bulgária", "Burkina Faso", "Burundi", "Butão", "Cabo Verde", "Camarões", "Camboja", "Canadá", "Catar", 
  "Cazaquistão", "Chade", "Chile", "China", "Chipre", "Colômbia", "Comores", "Congo", "Coreia do Norte", "Coreia do Sul", 
  "Costa do Marfim", "Costa Rica", "Croácia", "Cuba", "Dinamarca", "Djibouti", "Dominica", "Egito", "El Salvador", 
  "Emirados Árabes Unidos", "Equador", "Eritreia", "Eslováquia", "Eslovênia", "Espanha", "Estados Unidos", "Estônia", 
  "Etiópia", "Fiji", "Filipinas", "Finlândia", "França", "Gabão", "Gâmbia", "Gana", "Geórgia", "Granada", "Grécia", 
  "Guatemala", "Guiana", "Guiné", "Guiné-Bissau", "Guiné Equatorial", "Haiti", "Holanda", "Honduras", "Hungria", "Iêmen", 
  "Ilhas Marshall", "Ilhas Maurício", "Ilhas Salomão", "Índia", "Indonésia", "Irã", "Iraque", "Irlanda", "Islândia", 
  "Israel", "Itália", "Jamaica", "Japão", "Jordânia", "Kiribati", "Kuwait", "Laos", "Lesoto", "Letônia", "Líbano", 
  "Libéria", "Líbia", "Liechtenstein", "Lituânia", "Luxemburgo", "Macedônia do Norte", "Madagascar", "Malásia", "Malaui", 
  "Maldivas", "Mali", "Malta", "Marrocos", "Mauritânia", "México", "Micronésia", "Moçambique", "Moldávia", "Mônaco", 
  "Mongólia", "Montenegro", "Myanmar", "Namíbia", "Nauru", "Nepal", "Nicarágua", "Níger", "Nigéria", "Noruega", 
  "Nova Zelândia", "Omã", "Palau", "Panamá", "Papua-Nova Guiné", "Paquistão", "Paraguai", "Peru", "Polônia", "Portugal", 
  "Quênia", "Quirguistão", "Reino Unido", "República Centro-Africana", "República Checa", "República Democrática do Congo", 
  "República Dominicana", "Romênia", "Ruanda", "Rússia", "Samoa", "San Marino", "Santa Lúcia", "São Cristóvão e Névis", 
  "São Tomé e Príncipe", "São Vicente e Granadinas", "Seychelles", "Senegal", "Serra Leoa", "Sérvia", "Singapura", "Síria", 
  "Somália", "Sri Lanka", "Suazilândia", "Sudão", "Sudão do Sul", "Suécia", "Suíça", "Suriname", "Tailândia", "Taiwan", 
  "Tajiquistão", "Tanzânia", "Timor-Leste", "Togo", "Tonga", "Trinidad e Tobago", "Tunísia", "Turcomenistão", "Turquia", 
  "Tuvalu", "Ucrânia", "Uganda", "Uruguai", "Uzbequistão", "Vanuatu", "Vaticano", "Venezuela", "Vietnã", "Zâmbia", "Zimbábue"
];

export function TripBasicInfoForm({ trip, handleChange, isEditing = false }: TripBasicInfoFormProps) {
  const [destinationInputOpen, setDestinationInputOpen] = useState(false);
  const [searchDestination, setSearchDestination] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const showDates = !!trip.startDate || !!trip.endDate;
  
  useEffect(() => {
    // Carregar clientes selecionados ao montar o componente
    const loadSelectedClients = async () => {
      if (trip.clientIds && trip.clientIds.length > 0) {
        try {
          // Buscar todos os clientes do Supabase
          const clientPromises = trip.clientIds.map(id => clientService.getClientById(id));
          const clients = await Promise.all(clientPromises);
          // Filtrar nulos
          const validClients = clients.filter(Boolean) as Client[];
          // Se algum cliente não foi encontrado no Supabase, tenta localStorage
          if (validClients.length < trip.clientIds.length) {
            const savedClients = localStorage.getItem('voah_clients');
            if (savedClients) {
              const allClients = JSON.parse(savedClients);
              const missingIds = trip.clientIds.filter(id => !validClients.find(c => c.id === id));
              const localClients = allClients.filter((client: Client) => missingIds.includes(client.id));
              setSelectedClients([...validClients, ...localClients]);
              return;
            }
          }
          setSelectedClients(validClients);
        } catch (err) {
          // Fallback total para localStorage
          const savedClients = localStorage.getItem('voah_clients');
          if (savedClients) {
            const allClients = JSON.parse(savedClients);
            const selected = allClients.filter((client: Client) => trip.clientIds.includes(client.id));
            setSelectedClients(selected);
          }
        }
      } else {
        setSelectedClients([]);
      }
    };
    loadSelectedClients();
  }, [trip.clientIds]);

  const handleClientSearch = async (value: string) => {
    setClientSearch(value);
    if (!value.trim()) {
      setAvailableClients([]);
      return;
    }
    try {
      // Buscar clientes do Supabase
      const clients = await clientService.searchClients(value);
      const filtered = clients.filter((client: Client) =>
        !trip.clientIds?.includes(client.id)
      );
      setAvailableClients(filtered);
    } catch (error) {
      // Fallback para localStorage
      const savedClients = localStorage.getItem('voah_clients');
      if (savedClients) {
        const allClients = JSON.parse(savedClients);
        const filtered = allClients.filter((client: Client) =>
          client.name.toLowerCase().includes(value.toLowerCase()) &&
          !trip.clientIds?.includes(client.id)
        );
        setAvailableClients(filtered);
      }
    }
  };

  const handleAddClient = (client: Client) => {
    if (!trip.clientIds?.includes(client.id)) {
      const newClientIds = [...(trip.clientIds || []), client.id];
      handleChange('clientIds', newClientIds);
      setSelectedClients(prev => [...prev, client]);
      setClientSearch('');
      setAvailableClients([]);
    }
  };

  const handleRemoveClient = (clientId: string) => {
    const newClientIds = (trip.clientIds || []).filter(id => id !== clientId);
    handleChange('clientIds', newClientIds);
    setSelectedClients(prev => prev.filter(client => client.id !== clientId));
  };

  // Parse the destination field and ensure it's an array
  const destinations = Array.isArray(trip.destination) 
    ? trip.destination 
    : typeof trip.destination === 'string'
      ? trip.destination.split(',').map(d => d.trim()).filter(Boolean)
      : [];
  
  const handleAddDestination = (country: string) => {
    if (!destinations.includes(country)) {
      const newDestinations = [...destinations, country];
      handleChange('destination', newDestinations);
    }
    setSearchDestination("");
  };

  const handleRemoveDestination = (country: string) => {
    const newDestinations = destinations.filter(d => d !== country);
    handleChange('destination', newDestinations);
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    // Passar a string 'YYYY-MM-DD' diretamente
    handleChange(field, value);
  };

  const handleImageUpload = (images: string[]) => {
    const currentImages = trip.coverImages || [];
    handleChange('coverImages', [...currentImages, ...images]);
  };

  const filteredCountries = countries.filter(country => 
    country.toLowerCase().includes(searchDestination.toLowerCase()) && 
    !destinations.includes(country)
  );

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Informações da Viagem</h2>
        
        <div className="flex flex-col space-y-4">
          <div className="flex items-center gap-4">
            <label className="w-1/4 text-sm font-medium">Título</label>
            <Input
              value={trip.title || ''}
              onChange={e => handleChange('title', e.target.value)}
              placeholder="Nome da viagem"
              className="flex-1"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-1/4 text-sm font-medium">Destino</label>
            <div className="flex-1">
              <Popover open={destinationInputOpen} onOpenChange={setDestinationInputOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={destinationInputOpen}
                    className="w-full justify-between text-left"
                  >
                    {destinations.length > 0 ? `${destinations.length} países selecionados` : "Selecione os países"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Buscar país..." 
                      value={searchDestination}
                      onValueChange={setSearchDestination}
                    />
                    <CommandList>
                      <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
                      <CommandGroup>
                        {filteredCountries.map((country) => (
                          <CommandItem
                            key={country}
                            onSelect={() => handleAddDestination(country)}
                          >
                            {country}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              
              {destinations.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {destinations.map(country => (
                    <Badge key={country} variant="secondary" className="flex items-center gap-1">
                      {country}
                      <button
                        type="button"
                        className="rounded-full ml-1 hover:bg-background/80"
                        onClick={() => handleRemoveDestination(country)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="w-1/4 text-sm font-medium">Clientes</label>
            <div className="flex-1">
              <div className="relative">
                <Input
                  value={clientSearch}
                  onChange={(e) => handleClientSearch(e.target.value)}
                  placeholder="Buscar cliente..."
                  className="w-full"
                />
                {availableClients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {availableClients.map((client) => (
                      <div
                        key={client.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleAddClient(client)}
                      >
                        {client.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedClients.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedClients.map((client) => (
                    <Badge key={client.id} variant="secondary" className="flex items-center gap-1">
                      {client.name}
                      <button
                        type="button"
                        className="rounded-full ml-1 hover:bg-background/80"
                        onClick={() => handleRemoveClient(client.id)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="w-1/4 text-sm font-medium">Status</label>
            <div className="flex-1">
              <Select
                value={trip.status}
                onValueChange={(value: 'quotation' | 'itinerary' | 'day-by-day') => handleChange('status', value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quotation">Cotação</SelectItem>
                  <SelectItem value="itinerary">Roteiro</SelectItem>
                  <SelectItem value="day-by-day">Day-by-Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-1/4">
              <label className="text-sm font-medium">Datas</label>
            </div>
            <div className="flex-1 space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="use-defined-dates"
                  checked={showDates}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      handleChange('startDate', null);
                      handleChange('endDate', null);
                    } else {
                      handleChange('startDate', new Date());
                    }
                  }}
                />
                <label
                  htmlFor="use-defined-dates"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Usar datas definidas
                </label>
              </div>
              
              {showDates && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label>Data de início</Label>
                    <Input
                      type="date"
                      value={typeof trip.startDate === 'string' ? trip.startDate : (trip.startDate instanceof Date ? trip.startDate.toISOString().slice(0,10) : '')}
                      onChange={(e) => handleDateChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Data de fim</Label>
                    <Input
                      type="date"
                      value={typeof trip.endDate === 'string' ? trip.endDate : (trip.endDate instanceof Date ? trip.endDate.toISOString().slice(0,10) : '')}
                      onChange={(e) => handleDateChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <label className="w-1/4 text-sm font-medium pt-2">Observações</label>
            <Textarea
              value={trip.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Adicione informações gerais sobre a viagem"
              className="flex-1"
              rows={3}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <label className="w-1/4 text-sm font-medium">Imagens de Capa</label>
            <div className="flex-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsUploadModalOpen(true)}
                className="flex items-center gap-2"
              >
                <ImagePlus className="h-4 w-4" />
                {trip.coverImages && trip.coverImages.length > 0 
                  ? `Gerenciar imagens (${trip.coverImages.length})` 
                  : "Adicionar imagens de capa"}
              </Button>
              
              {trip.coverImages && trip.coverImages.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {trip.coverImages.slice(0, 3).map((image, index) => (
                    <div key={index} className="relative h-20 rounded overflow-hidden">
                      <img src={image} alt={`Imagem ${index + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {trip.coverImages.length > 3 && (
                    <div className="flex items-center justify-center h-20 bg-gray-100 rounded">
                      <span className="text-sm text-gray-500">+{trip.coverImages.length - 3} imagens</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <ImageManagerModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        images={trip.coverImages || []}
        onImagesChange={(images) => handleChange('coverImages', images)}
        contextId={trip.id || 'trip'}
        contextType="trip"
      />
    </div>
  );
} 